// Workspace: the active study session. Generation is the paid action, gated PER
// EXAM. The UI reflects entitlement for the selected exam (PaywallBanner +
// disabled button), but the authoritative refusal is server-side: generateQuestions
// checks the caller's entitlement for this exam before doing any work. If an
// unentitled user calls it directly, the callable throws permission-denied.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useExam } from '../context/ExamContext';
import { useSubscription } from '../context/SubscriptionContext';
import { generateQuestions } from '../lib/functions';
import QuestionCard from '../components/QuestionCard';
import PaywallBanner from '../components/PaywallBanner';

const COUNT_OPTIONS = [3, 5, 10];

export default function Workspace() {
  const { selectedExam } = useExam();
  const { isEntitled } = useSubscription();
  const [count, setCount] = useState(5);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const entitled = selectedExam ? isEntitled(selectedExam.id) : false;

  async function generate() {
    if (!selectedExam) return;
    setBusy(true);
    setErr(null);
    try {
      const generated = await generateQuestions(selectedExam.id, count);
      setItems((prev) => [...generated, ...prev]);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Generation failed.';
      setErr(
        msg.toLowerCase().includes('permission')
          ? 'You are not subscribed to this exam. Generation was refused.'
          : msg,
      );
    } finally {
      setBusy(false);
    }
  }

  function patchItem(next) {
    setItems((prev) => prev.map((it) => (it.itemId === next.itemId ? next : it)));
  }

  if (!selectedExam) {
    return (
      <>
        <div className="page-head">
          <span className="eyebrow">Workspace</span>
          <h1>No exam selected</h1>
        </div>
        <div className="empty">
          Choose an exam on the <Link to="/dashboard">Dashboard</Link> to begin a study session.
        </div>
      </>
    );
  }

  return (
    <>
      <div className="page-head">
        <span className="eyebrow">Workspace — {selectedExam.name}</span>
        <h1>Study session</h1>
        <p>
          Questions are generated from validated claims, weighted to the {selectedExam.name}{' '}
          blueprint. Each explanation shows the underlying knowledge and its provenance.
        </p>
      </div>

      {!entitled && (
        <div style={{ marginBottom: '1.5rem' }}>
          <PaywallBanner examId={selectedExam.id} examName={selectedExam.name} />
        </div>
      )}

      <div className="card">
        <div className="row between wrap">
          <div className="row wrap" style={{ gap: '0.75rem' }}>
            <span className="eyebrow" style={{ alignSelf: 'center' }}>
              Questions
            </span>
            {COUNT_OPTIONS.map((n) => (
              <button
                key={n}
                className={`btn btn-sm${count === n ? ' btn-primary' : ''}`}
                onClick={() => setCount(n)}
                disabled={busy}
              >
                {n}
              </button>
            ))}
          </div>
          <button className="btn btn-primary" onClick={generate} disabled={busy || !entitled}>
            {busy ? 'Generating…' : 'Generate questions'}
          </button>
        </div>
        {err && (
          <div className="error-note" style={{ marginTop: '0.85rem' }}>
            {err}
          </div>
        )}
        {!entitled && (
          <div className="muted" style={{ marginTop: '0.6rem', fontSize: '0.82rem' }}>
            Generation is disabled until you subscribe to this exam.
          </div>
        )}
      </div>

      {items.length > 0 && (
        <div className="stack" style={{ marginTop: '1.5rem' }}>
          {items.map((it) => (
            <QuestionCard key={it.itemId} item={it} onChanged={patchItem} />
          ))}
        </div>
      )}

      {items.length === 0 && entitled && (
        <div className="empty" style={{ marginTop: '1.5rem' }}>
          No questions yet. Generate a set to begin. Everything you generate is saved to your
          Library automatically.
        </div>
      )}
    </>
  );
}
